import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  CatalogExistingBiblioSummary,
  type CatalogMatchBiblioSummary,
} from "@/components/catalog/CatalogExistingBiblioSummary";
import { AddCopyItemDefaultsGrid } from "@/components/catalog/AddCopyItemDefaultsGrid";
import { CatalogMobileActionBar } from "@/components/catalog/CatalogMobileActionBar";
import { CopyDetailsEditor, type ItemCopyData } from "@/components/catalog/CopyDetailsEditor";
import type { CreateCatalogRequest, CatalogSettings } from "@/lib/api/catalog";
import type { ItemType, Collection } from "@/lib/api/item-types";
import type { Location } from "@/lib/api/locations";
import type { BookCondition } from "@/lib/api/book-conditions";
import type { ItemStatus } from "@/lib/api/item-statuses";
import type { Branch } from "@/lib/api/branches";
import { Loader2, Package } from "lucide-react";

type ItemDefaults = CreateCatalogRequest["itemDefaults"];

export interface AddCopyMatchSummary {
  biblio: CatalogMatchBiblioSummary;
  existingCopyCount: number;
  biblioId: string;
  matchLabel?: "ISBN" | "ISSN";
}

interface AddCopyFormSectionProps {
  copies: number;
  onCopiesChange: (copies: number) => void;
  copyData: ItemCopyData[];
  onCopyDataChange: (data: ItemCopyData[]) => void;
  itemDefaults: ItemDefaults;
  onItemDefaultsChange: (defaults: ItemDefaults) => void;
  conditions: BookCondition[];
  itemStatuses: ItemStatus[];
  locations: Location[];
  branches: Branch[];
  itemTypes: ItemType[];
  collections: Collection[];
  catalogSettings: CatalogSettings;
  highlightCopyAcquisition?: boolean[];
  submitLabel?: string;
  onSubmit: () => void;
  loading?: boolean;
  matchSummary?: AddCopyMatchSummary;
  mobileActions?: ReactNode;
  showDesktopSubmit?: boolean;
}

export function AddCopyFormSection({
  copies,
  onCopiesChange,
  copyData,
  onCopyDataChange,
  itemDefaults,
  onItemDefaultsChange,
  conditions,
  itemStatuses,
  locations,
  branches,
  itemTypes,
  catalogSettings,
  highlightCopyAcquisition,
  submitLabel = "Add copy",
  onSubmit,
  loading = false,
  matchSummary,
  mobileActions,
  showDesktopSubmit = true,
}: AddCopyFormSectionProps) {
  const isManualAccession = catalogSettings.accessionNumberMode === "MANUAL";
  const submitButton = (
    <Button
      type="button"
      className="min-h-11 w-full lg:w-auto"
      onClick={onSubmit}
      disabled={loading}
    >
      {loading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Adding…
        </>
      ) : (
        submitLabel
      )}
    </Button>
  );

  return (
    <div className="space-y-6 pb-24 lg:pb-0">
      {matchSummary ? (
        <CatalogExistingBiblioSummary
          biblio={matchSummary.biblio}
          existingCopyCount={matchSummary.existingCopyCount}
          biblioId={matchSummary.biblioId}
          matchLabel={matchSummary.matchLabel}
        />
      ) : null}

      <Card className="overflow-hidden bg-white shadow-sm">
        <div className="space-y-6 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Package className="h-5 w-5 text-primary" />
            Add Copy
          </h2>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <AddCopyItemDefaultsGrid
              copies={copies}
              onCopiesChange={onCopiesChange}
              itemDefaults={itemDefaults}
              onItemDefaultsChange={onItemDefaultsChange}
              itemTypes={itemTypes}
              locations={locations}
              conditions={conditions}
              itemStatuses={itemStatuses}
            />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="mb-3 text-sm font-medium text-gray-900">
              Copy Details
              <span className="ml-2 text-xs font-normal text-red-500">
                * Acquisition mode required for each copy
              </span>
              {isManualAccession ? (
                <span className="ml-2 text-xs font-normal text-red-500">
                  * Accession numbers required
                </span>
              ) : null}
            </h3>
            <CopyDetailsEditor
              copies={copies}
              copyData={copyData}
              onCopyDataChange={onCopyDataChange}
              conditions={conditions}
              itemStatuses={itemStatuses}
              locations={locations}
              branches={branches}
              isManualMode={isManualAccession}
              defaultCondition={itemDefaults.conditionCode}
              defaultStatus={itemDefaults.statusCode || "AVAILABLE"}
              defaultLocationId={itemDefaults.locationId}
              defaultBranchId={itemDefaults.branchId}
              highlightCopyAcquisition={highlightCopyAcquisition}
            />
          </div>

          {showDesktopSubmit ? (
            <div className="hidden justify-end lg:flex">{submitButton}</div>
          ) : null}
        </div>
      </Card>

      <CatalogMobileActionBar>{mobileActions ?? submitButton}</CatalogMobileActionBar>
    </div>
  );
}
